import {
  Box,
  Image,
  Text,
  Heading,
  Stack,
  Container,
  SimpleGrid,
  Badge,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  UnorderedList,
  OrderedList,
  ListItem,
  Divider,
} from "@chakra-ui/react";

import Navbar from "./Navbar";
import { useAuth } from './AuthContext';

import HeroImage from "./images/stamps.png";

const SecurityPage = () => {
  const { user } = useAuth();

  const auth0Config = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
    redirectUri: process.env.REACT_APP_AUTH0_REDIRECT_URI,
    sessionEndpoint: process.env.REACT_APP_AUTH_SESSION_ENDPOINT,
  };

  return (
    <Box>
      <Navbar />

      {/* Background image */}
      <Image
        src={HeroImage}
        alt="Hero Watermark"
        width="100%"
        height="100%"
        objectFit="cover"
        opacity={0.30}
        position="absolute"
        top={0}
        left={0}
        zIndex={-1}
        filter="blur(1px)"
      />

      {!user ? (
        <Container maxW="container.md" py={20}>
          <Box
            bg="white"
            p={6}
            borderRadius="md"
            boxShadow="md"
            textAlign="center"
          >
            <Text fontSize="md">
              You must be logged in to view this content.
            </Text>
          </Box>
        </Container>
      ) : (
        <Container maxW="container.xl" py={8}>
          <Stack spacing={6}>
            {/* Header */}
            <Box
              bg="rgba(255,255,255,0.95)"
              p={6}
              borderRadius="lg"
              boxShadow="xl"
              textAlign="center"
            >
              <Heading
                fontSize="3xl"
                mb={2}
                color="gray.800"
                fontWeight="bold"
              >
                Security & Authentication
              </Heading>
              <Text fontSize="md" color="gray.600">
                Auth0 configuration and user permission management
              </Text>
            </Box>

            {/* Auth0 Configuration */}
            <Box
              bg="rgba(255, 255, 255, 0.9)"
              p={6}
              borderRadius="md"
              boxShadow="md"
              border="1px solid #805ad5"
            >
              <Heading size="md" mb={4} color="purple.600">
                Auth0 Configuration
              </Heading>
              <Stack spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Domain:</Text>
                  <Code p={2} borderRadius="md" display="block">
                    {auth0Config.domain}
                  </Code>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Client ID:</Text>
                  <Code p={2} borderRadius="md" display="block">
                    {auth0Config.clientId}
                  </Code>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Redirect URI:</Text>
                  <Code p={2} borderRadius="md" display="block">
                    {auth0Config.redirectUri}
                  </Code>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Session Endpoint:</Text>
                  <Code p={2} borderRadius="md" display="block">
                    {auth0Config.sessionEndpoint}
                  </Code>
                </Box>
                <Box mt={4}>
                  <Text fontSize="sm" color="gray.600" fontStyle="italic">
                    Manage Auth0 settings at:{" "}
                    <Text
                      as="a"
                      href="https://manage.auth0.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="purple.600"
                      _hover={{ textDecoration: "underline" }}
                    >
                      https://manage.auth0.com
                    </Text>
                  </Text>
                </Box>
              </Stack>
            </Box>

            {/* Current User Information */}
            <Box
              bg="rgba(255, 255, 255, 0.9)"
              p={6}
              borderRadius="md"
              boxShadow="md"
              border="1px solid #3182ce"
            >
              <Heading size="md" mb={4} color="blue.600">
                Current User Information
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={1}>Name:</Text>
                  <Text>{user?.name}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={1}>Email:</Text>
                  <Text>{user?.email}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={1}>Auth0 Subject:</Text>
                  <Code fontSize="sm">{user?.auth0Sub}</Code>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={1}>Platform User ID:</Text>
                  <Code fontSize="sm">{user?.platformUser?._id}</Code>
                </Box>
              </SimpleGrid>

              <Box mt={6}>
                <Text fontWeight="bold" mb={2}>Permissions:</Text>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {user?.platformUser?.isActive === "true" && (
                    <Badge colorScheme="green">Active</Badge>
                  )}
                  {user?.platformUser?.isDeveloper === "true" && (
                    <Badge colorScheme="blue">Developer</Badge>
                  )}
                  {user?.platformUser?.isAdmin === "true" && (
                    <Badge colorScheme="purple">Admin</Badge>
                  )}
                  {user?.platformUser?.isSuperAdmin === "true" && (
                    <Badge colorScheme="orange">Super Admin</Badge>
                  )}
                </Stack>
              </Box>

              {user?.platformUser?.roles && user.platformUser.roles.length > 0 && (
                <Box mt={6}>
                  <Text fontWeight="bold" mb={2}>Organization Roles:</Text>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Organization ID</Th>
                        <Th>Role</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {user.platformUser.roles.map((roleObj, index) => (
                        <Tr key={index}>
                          <Td>
                            <Code fontSize="xs">{roleObj.organisationId}</Code>
                          </Td>
                          <Td>
                            <Badge colorScheme="teal">{roleObj.role}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>

            {/* Authentication Flow */}
            <Box
              bg="rgba(255, 255, 255, 0.9)"
              p={6}
              borderRadius="md"
              boxShadow="md"
              border="1px solid #38a169"
            >
              <Heading size="md" mb={4} color="green.600">
                Authentication Flow
              </Heading>
              <Stack spacing={3}>
                <Box>
                  <Badge colorScheme="green" mb={2}>Step 1</Badge>
                  <Text fontSize="sm">
                    User clicks "Log in" button → Redirected to Auth0 login page
                  </Text>
                </Box>
                <Box>
                  <Badge colorScheme="green" mb={2}>Step 2</Badge>
                  <Text fontSize="sm">
                    User authenticates with Auth0 → Receives ID token
                  </Text>
                </Box>
                <Box>
                  <Badge colorScheme="green" mb={2}>Step 3</Badge>
                  <Text fontSize="sm">
                    App redirected to /callback → ID token sent to session endpoint
                  </Text>
                </Box>
                <Box>
                  <Badge colorScheme="green" mb={2}>Step 4</Badge>
                  <Text fontSize="sm">
                    Session endpoint validates token → Returns platform user data
                  </Text>
                </Box>
                <Box>
                  <Badge colorScheme="green" mb={2}>Step 5</Badge>
                  <Text fontSize="sm">
                    User data stored in AuthContext → User logged in
                  </Text>
                </Box>
              </Stack>
            </Box>

            <Divider my={8} borderColor="gray.300" />

            {/* Enterprise SSO Documentation */}
            <Box
              bg="rgba(255, 255, 255, 0.95)"
              p={8}
              borderRadius="lg"
              boxShadow="xl"
            >
              <Heading fontSize="2xl" mb={6} color="gray.800">
                Authentication & Single Sign-On (SSO)
              </Heading>

              <Stack spacing={6}>
                <Box>
                  <Text fontSize="md" lineHeight="1.8">
                    mrc.ai uses enterprise-grade authentication provided by{" "}
                    <Text as="span" fontWeight="bold">Auth0</Text>, allowing organisations to securely
                    connect using their existing identity providers such as{" "}
                    <Text as="span" fontWeight="bold">Microsoft Entra ID (Azure AD)</Text> or{" "}
                    <Text as="span" fontWeight="bold">Okta</Text>.
                  </Text>
                  <Text fontSize="md" mt={3} lineHeight="1.8">
                    This means users authenticate using the same corporate credentials they already
                    use at work — mrc.ai does not issue or manage passwords.
                  </Text>
                </Box>

                {/* How Login Works */}
                <Box>
                  <Heading size="md" mb={3} color="blue.600">
                    How login works
                  </Heading>
                  <OrderedList spacing={2} fontSize="sm">
                    <ListItem>User accesses mrc.ai</ListItem>
                    <ListItem>The browser is redirected to Auth0</ListItem>
                    <ListItem>
                      Auth0 redirects the user to their organisation's identity provider
                      (e.g. Entra ID or Okta)
                    </ListItem>
                    <ListItem>
                      If the user already has an active enterprise session, authentication
                      completes automatically without prompting for credentials
                    </ListItem>
                    <ListItem>
                      A secure, signed token is returned to mrc.ai and the user is logged in
                    </ListItem>
                  </OrderedList>
                  <Text fontSize="sm" mt={3} fontStyle="italic" color="gray.600">
                    This behaviour is often referred to as{" "}
                    <Text as="span" fontWeight="bold">Single Sign-On (SSO)</Text> or{" "}
                    <Text as="span" fontWeight="bold">silent authentication</Text>.
                  </Text>
                </Box>

                {/* Enterprise Security Controls */}
                <Box>
                  <Heading size="md" mb={3} color="purple.600">
                    Enterprise security controls
                  </Heading>
                  <Text fontSize="sm" mb={2}>
                    All security policies remain fully controlled by the organisation's identity provider.
                  </Text>
                  <UnorderedList spacing={1} fontSize="sm" pl={4}>
                    <ListItem>Multi-factor authentication (MFA)</ListItem>
                    <ListItem>Conditional access policies</ListItem>
                    <ListItem>Device compliance requirements</ListItem>
                    <ListItem>Network and location restrictions</ListItem>
                    <ListItem>Session lifetime and reauthentication rules</ListItem>
                  </UnorderedList>
                  <Text fontSize="sm" mt={3} fontStyle="italic" color="gray.600">
                    mrc.ai inherits and enforces these controls and does not weaken or bypass them in any way.
                  </Text>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {/* Passwords & Credentials */}
                  <Box
                    bg="blue.50"
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    <Heading size="sm" mb={2} color="blue.700">
                      Passwords & credentials
                    </Heading>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                      mrc.ai never stores or processes user passwords.
                    </Text>
                    <Text fontSize="sm">
                      Authentication is performed entirely by the organisation's identity provider
                      and validated using industry-standard protocols (<Text as="span" fontWeight="bold">OIDC</Text> or{" "}
                      <Text as="span" fontWeight="bold">SAML 2.0</Text>).
                    </Text>
                  </Box>

                  {/* Access Revocation */}
                  <Box
                    bg="red.50"
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Heading size="sm" mb={2} color="red.700">
                      Access revocation
                    </Heading>
                    <Text fontSize="sm" mb={2}>
                      User access can be revoked centrally by disabling or removing the user
                      in the organisation's identity provider.
                    </Text>
                    <Text fontSize="sm">
                      Once access is revoked at the identity provider, the user can no longer
                      authenticate to mrc.ai.
                    </Text>
                  </Box>

                  {/* Authorization & Roles */}
                  <Box
                    bg="green.50"
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.200"
                  >
                    <Heading size="sm" mb={2} color="green.700">
                      Authorisation & roles
                    </Heading>
                    <Text fontSize="sm" mb={2}>
                      Authentication confirms <Text as="span" fontStyle="italic">who</Text> the user is.
                      Authorisation determines <Text as="span" fontStyle="italic">what</Text> they are allowed to do.
                    </Text>
                    <Text fontSize="sm">
                      mrc.ai evaluates user roles and permissions on each request and enforces
                      access controls at the API level, not only in the user interface.
                    </Text>
                  </Box>

                  {/* Tenant Isolation */}
                  <Box
                    bg="purple.50"
                    p={4}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="purple.200"
                  >
                    <Heading size="sm" mb={2} color="purple.700">
                      Tenant isolation
                    </Heading>
                    <Text fontSize="sm" mb={2}>
                      Each organisation using mrc.ai is logically isolated.
                    </Text>
                    <Text fontSize="sm">
                      Identity tokens and permissions are scoped to a single organisation and
                      cannot be used to access data belonging to another tenant.
                    </Text>
                  </Box>
                </SimpleGrid>

                {/* Logout Behaviour */}
                <Box>
                  <Heading size="md" mb={3} color="orange.600">
                    Logout behaviour
                  </Heading>
                  <Text fontSize="sm" mb={2}>
                    Logging out of mrc.ai ends the application session.
                  </Text>
                  <Text fontSize="sm">
                    Enterprise login sessions (for example, Microsoft Entra ID) are governed
                    by the organisation's own security policies and session controls, in line
                    with standard federated SSO behaviour.
                  </Text>
                </Box>

                {/* Summary */}
                <Box
                  bg="gray.50"
                  p={6}
                  borderRadius="md"
                  border="2px solid"
                  borderColor="gray.300"
                >
                  <Heading size="md" mb={4} color="gray.800">
                    In summary
                  </Heading>
                  <UnorderedList spacing={2} fontSize="sm">
                    <ListItem>Enterprise SSO via Entra ID, Okta, or SAML/OIDC providers</ListItem>
                    <ListItem>No passwords stored or managed by mrc.ai</ListItem>
                    <ListItem>MFA and conditional access enforced by the organisation</ListItem>
                    <ListItem>Short-lived, signed tokens used for API access</ListItem>
                    <ListItem>Strong tenant isolation and auditability</ListItem>
                  </UnorderedList>
                </Box>
              </Stack>
            </Box>

            {/* Auth0 Certification Footer */}
            <Box textAlign="center" pt={4} pb={2}>
              <Text fontSize="sm" color="gray.600" opacity={0.7}>
                Authentication services are provided by Auth0, a certified identity platform
                supporting SOC 2, ISO 27001, and industry-standard security controls.
              </Text>
            </Box>
          </Stack>
        </Container>
      )}
    </Box>
  );
};

export default SecurityPage;
